import { clsx } from 'clsx'
import { type JSXInternal } from 'preact/src/jsx'

import { Typography } from '../typography'
import { Spinner } from '../spinner'

type ButtonVariants = 'primary' | 'secondary' | 'error'
type ButtonSizes = 'big' | 'normal' | 'small'

type Props = Omit<JSXInternal.HTMLAttributes<HTMLButtonElement>, 'size'> & {
	className?: string
	disabled?: boolean
	fullWidth?: boolean
	href?: string
	isLoading?: boolean
	size?: ButtonSizes
	text?: string
	variant?: ButtonVariants
	textColor?: string
	backgroundColor?: string
}

const styles = {
	defaultButton: {
		primary: 'bg-product hover:bg-product-hover disabled:bg-black-400 gap-1',
		secondary:
			'border border-product bg-transparent hover:border-product-hover hover:text-product-hover disabled:border-black-400',
		error: 'bg-error hover:bg-error-hover disabled:bg-black-400'
	},
	disabledText: {
		primary: 'text-black-800',
		secondary: 'text-black-400',
		error: 'text-black-800'
	},
	textColors: {
		primary: 'text-black-900',
		secondary: 'text-product group-hover:text-product-hover',
		error: 'text-white'
	},
	sizes: {
		big: 'h-[55px] rounded px-4.5 py-4',
		normal: 'h-[36px] rounded px-3.5 py-2.5',
		small: 'h-[24px] rounded px-2.5 py-1.5'
	},
	typography: {
		big: 'label1',
		normal: 'label3',
		small: 'label4'
	}
} as const

const Button = ({
	className,
	disabled = false,
	fullWidth = false,
	isLoading = false,
	size = 'normal',
	text,
	variant = 'primary',
	textColor,
	backgroundColor,
	children,
	href,
	...rest
}: Props) => {
	const computedTextColor =
		textColor ||
		(disabled ? styles.disabledText[variant] : styles.textColors[variant])

	const buttonClasses = clsx(
		'group flex items-center justify-center transition-all duration-300 :focus-within:outline-none :focus-visible:outline-none',
		{
			'w-full': fullWidth
		},
		backgroundColor || styles.defaultButton[variant],
		styles.sizes[size],
		className
	)

	const spinnerClasses =
		variant === 'error'
			? 'fill-white text-black-100'
			: 'fill-black-900 text-black-900'

	return (
		<button
			aria-busy={isLoading}
			class={buttonClasses}
			disabled={disabled}
			type='button'
			{...rest}
		>
			{children ?? (
				<Typography
					class={clsx({ 'mr-2.5': isLoading }, { 'cursor-pointer': !disabled })}
					color={computedTextColor}
					text={text!}
					variant={styles.typography[size]}
				/>
			)}
			{isLoading && <Spinner className={spinnerClasses} />}
		</button>
	)
}

export { Button }
